<template>
  <div>
    <div @click="handleDialogOpen" class="flex flex-row">
      <IconField icon-position="left">
        <InputIcon class="pi pi-search"></InputIcon>
        <InputText :placeholder="placeholder" />
      </IconField>
    </div>

    <!-- Call fillPlant via ref; because table missing first time filters change -->
    <Dialog
      v-model:visible="dialogIsOpen"
      position="top"
      :header="$t('features.search_cart.search')"
      :style="{ width: '80rem' }"
      modal
      :draggable="false"
      @show="handleShowDialog"
    >
      <div class="flex flex-row mb-5 gap-2">
        <div class="flex-auto">
          <div v-if="complexActive" class="flex items-center gap-1">
            <span class="text-lg font-semibold">Filter </span>
            <FilterAsChips :query-object="complexParams" />
          </div>

          <AutoComplete
            fluid
            v-else
            ref="search-query-form-input"
            v-model="searchQuery"
            :suggestions="searchSuggestions"
            @update:model-value="inputSearchQueryUpdated"
            @complete="updateSearchSuggestions"
            :placeholder="$t('features.search_cart.search')"
            class="w-1/2"
            :min-length="3"
            :showEmptyMessage="false"
          />
        </div>
        <div class="justify-end flex gap-2 justify-items-end">
          <Button @click="onReset" outlined>
            {{ $t('global.reset') }}
          </Button>
          <Button @click="dialogIsOpen = false" severity="secondary">
            {{ $t('global.close') }}
          </Button>
        </div>
      </div>
      <plants-overview-table
        v-if="hasAnyFilterActive"
        ref="plant_table"
        :resource-type="resourceTypeName"
        :company="company"
        @row-opened="dialogIsOpen = false"
        :desired-table-fields="shownFields"
        :filters="filters"
        @total-items="(ti) => (totalItems = ti)"
        :create-promise-for-paginated-response="
          createPromiseForPaginatedResponse
        "
      />
      <Message v-else>
        {{ $t('features.search_cart.type_to_start') }}
      </Message>
      <template #footer>
        <Button @click="goToPlantList" outlined>
          {{ $t('features.search_cart.advanced_search') }}
        </Button>
        <Button severity="secondary" @click="dialogIsOpen = false">
          {{ $t('global.close') }}
        </Button>
      </template>
    </Dialog>
  </div>
</template>

<script lang="ts">
import { useCompanyStore } from '@/stores/CompanyStore.ts';
import type Company from '@/domain/model/business/Company';
import { sendDialogOpenEvent } from '@/infrastructure/support/Analytics.ts';
import FilterAsChips from '@/presentation/pages/plants/widgets/FilterAsChips.vue';
import ResourceType from '@/presentation/pages/plants/widgets/ResourceType.ts';
import PaginatedItemsHelper from '@/presentation/pages/resources/PaginatedItemsHelper.ts';
import type PaginatedResponse from '@/presentation/pages/resources/PaginatedResponse.ts';
import PlantsOverviewTable from '@/presentation/pages/resources/PlantsOverviewTable.vue';
import TweakParamsForPlantCall from '@/presentation/pages/resources/TweakParamsForPlantCall';
import { useSearchCartStore } from '@/stores/SearchCartStore.ts';
import _ from 'lodash';
import SearchSuggestionsApi from '@/data/filters/SearchSuggestionsApi.ts';

export default {
  name: 'SearchCart',
  components: { FilterAsChips, PlantsOverviewTable },
  mixins: [TweakParamsForPlantCall],
  data() {
    return {
      shownFields: [],
      filters: null,
      totalItems: null,

      complexActive: false,
      searchQuery: '',
      complexParams: null,
      resourceTypeName: ResourceType.PLANTS,
      company: useCompanyStore().company as Company,
      unsubscribeFromStoreCall: null,
      dialogIsOpen: false,
      debouncedQueryUpdate: null,

      searchSuggestions: [],
    };
  },
  mounted() {
    this.$watch(
      () => useCompanyStore().company,
      (company) => (this.company = company),
    );
    const storeSearchCartState = useSearchCartStore().searchCart;
    this.complexActive = storeSearchCartState.complex;

    if (storeSearchCartState.complex) {
      this.resourceTypeName = storeSearchCartState.resourceTypeName;
      this.complexParams = storeSearchCartState.complexParams;
      this.filters = this.complexParams;
    } else {
      this.resourceTypeName = ResourceType.PLANTS;
      this.filters = storeSearchCartState.simpleParams;
      this.searchQuery = this.filters.everywhere;
    }
    this.shownFields = this.determineShownFields();

    this.unsubscribeFromStoreCall = this.$watch(
      () => useSearchCartStore().searchCart,
      (searchCart) => {
        if (searchCart.complex) {
          this.complexActive = true;
          this.complexParams = searchCart.complexParams;
          this.resourceTypeName = searchCart.resourceTypeName;
          this.shownFields = this.determineShownFields();
          this.filters = this.complexParams;
          this.updateTotalItems();
        }
      },
    );

    this.updateTotalItems();
  },
  unmounted() {
    this.unsubscribeFromStoreCall();
  },
  computed: {
    hasAnyFilterActive() {
      if (Object.entries(this.filters).length == 0) {
        return false;
      }
      if (
        Object.entries(this.filters).length == 1 &&
        Object.hasOwn(this.filters, 'everywhere') &&
        this.filters.everywhere === ''
      ) {
        return false;
      }
      return true;
    },
    placeholder() {
      return this.totalItems
        ? this.$t('features.search_cart.search') + ` (${this.totalItems})`
        : this.$t('features.search_cart.search');
    },
  },
  methods: {
    determineShownFields() {
      // noinspection JSUnreachableSwitchBranches FP lies!
      switch (this.resourceTypeName) {
        case ResourceType.PLANTS:
          return ['issues', 'name', 'city', 'street', 'tags'];
        case ResourceType.ISSUES:
          return [
            'new_issue',
            'found_on',
            'snoozed_until',
            'name',
            'create_ticket',
            'tickets',
            'existing_issues',
          ];
        case ResourceType.TICKETS:
          return ['title', 'status', 'assignees', 'created_at', 'closed_on'];
        case ResourceType.PERFORMANCES:
          return [
            'current_promise_status',
            'status_since',
            'difference_till_now_pct',
            'difference_till_now_wh',
            'previous_promise_statuses',
            'issues',
            'name',
          ];
      }
    },
    createPromiseForPaginatedResponse(params, fields) {
      // noinspection JSUnreachableSwitchBranches FP lies!
      switch (this.resourceTypeName) {
        case ResourceType.TICKETS:
          return PaginatedItemsHelper.createPromiseForPaginatedResponseForTickets(
            this.company,
            params,
            fields,
          );
        case ResourceType.ISSUES:
          return PaginatedItemsHelper.createPromiseForPaginatedResponseForIssues(
            this.company,
            params,
            fields,
          );
        case ResourceType.PERFORMANCES:
          return PaginatedItemsHelper.createPromiseForPaginatedResponseForPerformance(
            this.company,
            params,
            fields,
          );
        default:
          return PaginatedItemsHelper.createPromiseForPaginatedResponseForPlants(
            this.company,
            params,
            fields,
          );
      }
    },
    handleDialogOpen() {
      this.dialogIsOpen = true;
      sendDialogOpenEvent('top_bar', 'search_cart');
    },
    handleShowDialog() {
      if (this.hasAnyFilterActive) {
        (this.$refs.plant_table as any).fillPlants();
      }
    },
    updateTotalItems() {
      if (!this.hasAnyFilterActive) {
        this.totalItems = null;
        return;
      }

      this.createPromiseForPaginatedResponse(
        this.tweakParamsForPlantsCall(this.filters),
        [],
      ).then((pr: PaginatedResponse) => (this.totalItems = pr.totalItems));
    },
    goToPlantList() {
      this.dialogIsOpen = false;
      this.$router.push({ name: 'AllPlants' });
    },
    inputSearchQueryUpdated() {
      const poTable = this.$refs.plant_table as typeof PlantsOverviewTable;
      poTable?.handleUpdateComing();

      if (this.debouncedQueryUpdate) {
        this.debouncedQueryUpdate.cancel();
      }

      this.debouncedQueryUpdate = _.debounce(() => {
        this.filters =
          this.searchQuery != null && this.searchQuery != ''
            ? { everywhere: this.searchQuery }
            : {};
        useSearchCartStore().setSimpleSearchCart(this.filters);
      }, 400);
      this.debouncedQueryUpdate();
    },
    updateSearchSuggestions() {
      new SearchSuggestionsApi()
        .getSearchSuggestions(this.company, this.searchQuery)
        .then((rsp) => {
          this.searchSuggestions = rsp.data.data.streets_starting_with_query
            .slice(0, 5)
            .map((i) => i.value);
        });
    },
    onReset() {
      this.complexActive = false;
      this.searchQuery = '';
      this.resourceTypeName = ResourceType.PLANTS;
      this.shownFields = this.determineShownFields();
      this.filters = {};
      this.totalItems = null;
      useSearchCartStore().setSimpleSearchCart(this.filters);
    },
  },
};
</script>

<style scoped></style>
